import { cloneDeep, update } from 'lodash';
import { PRODUCT_CLASSES, PRODUCT_UNITS } from '../../../Constants/product.constants';
// eslint-disable-next-line object-curly-newline
import { imageToBase64 } from '../../../Helpers';
import { notificationServices } from '../../../Services';

const productConfig = {
	initialState: {
		fields: {
			name: '',
			description: '',
			brand: null,
			category: null,
			subCategory: null,
			tax: 0,
			hsnCode: '',
			veg: true,
			image: null,
			price: null,
			density: null,
			densityUnit: '',
			menuTags: [],
			productTags: [],
			productType: '',
			perUnit: '',
			productClass: PRODUCT_CLASSES.map((productClass) => {
				return {
					...productClass,
					isApplied: false,
				};
			}),
			productUnits: PRODUCT_UNITS?.map((productUnit) => {
				return {
					...productUnit,
					isApplied: false,
				};
			}),
			purchaseUnits: PRODUCT_UNITS?.map((productUnit) => {
				return {
					...productUnit,
					isApplied: false,
				};
			}),
		},
		menuTags: ['Kids Menu', 'Healthy Menu', 'Subscription Menu'],
		productTags: [
			'High in Calories',
			'Pre Workout',
			'Spice-Low',
			'Spice-Medium',
			'Spice-High',
		],
	},
	mandatoryFields: {
		name: true,
		description: false,
		brand: true,
		category: true,
		subCategory: true,
		tax: true,
		hsnCode: true,
		veg: true,
		image: true,
		price: true,
		productClass: true,
		productUnit: true,
		purchaseUnits: false,
	},
	serializer: (state, data) => {
		const defaultProduct = data.variants.find((variant) => {
			return variant.isDefault;
		});
		const disabledUnits =
			data?.measurement === 'US'
				? ['gram', 'litre', 'ml']
				: ['oz', 'floz'];
		return update(cloneDeep(state), 'fields', () => {
			const newState = {
				name: data.productName,
				description: data.productDescription,
				tax: data.tax,
				image: data.productImageUrl,
				category: data.categoryId,
				subCategory: data.subCategoryId,
				brand: data.brandId,
				price: defaultProduct ? defaultProduct.price : null,
				density: data?.density,
				densityUnit: data?.densityUnit,
				perUnit: data?.unit === 'floz' ? 'Gal' : 'ltr',
				veg: data.isVeg,
				hsnCode: data?.hsnCode,
				menuTags: data.menuTags ? data.menuTags.split(',') : [],
				productTags: data.tags ? data.tags.split(',') : [],
				productType: data.productType,
				unit: data.productUnit,
				productUnits: PRODUCT_UNITS?.map((productUnit) => {
					return {
						...productUnit,
						isApplied: data?.unit === productUnit?.id,
					};
				})?.filter((ele) => {
					return !disabledUnits?.includes(ele?.id);
				}),
				purchaseUnits: PRODUCT_UNITS?.map((productUnit) => {
					return {
						...productUnit,
						isApplied: data?.purchaseUnit === productUnit?.id,
					};
				})?.filter((ele) => {
					return !disabledUnits?.includes(ele?.id);
				}),
				productClass: PRODUCT_CLASSES.map((productClass) => {
					return {
						...productClass,
						isApplied:
							productClass.name?.toLowerCase() ===
							data.skuType?.toLowerCase(),
					};
				}),
			};
			return newState;
		});
	},
	deserializer: (state) => {
		const clonedState = cloneDeep(state);
		const req = {};
		// let productDensity = getUnitConvertedQuantity(
		// clonedState.fields.density,
		// clonedState.fields.densityUnit,
		// true
		// );
		const selectedPurchaseUnit = clonedState?.fields?.purchaseUnits?.reduce(
			(finalUnit, currUnit) => {
				if (currUnit?.isApplied) {
					return currUnit?.id;
				}
				return finalUnit;
			},
			''
		);
		req.data = {
			productName: clonedState.fields.name,
			productDescription: clonedState.fields.description,
			brandId: clonedState.fields.brand,
			productImageUrl: clonedState.fields.image,
			isVeg: clonedState.fields.veg,
			density: clonedState.fields.density,
			categoryId: clonedState.fields.category,
			subCategoryId: clonedState.fields.subCategory,
			tax: clonedState.fields.tax?.toString(),
			hsnCode: clonedState.fields.hsnCode,
			cost: clonedState.fields.price,
			menuTags: clonedState.fields.menuTags.join(','),
			tags: clonedState.fields.productTags.join(','),
			unit: clonedState?.fields?.productUnits?.reduce(
				(finalUnit, currUnit) => {
					if (currUnit?.isApplied) {
						return currUnit?.id;
					}
					return finalUnit;
				},
				''
			),
			purchaseUnit: selectedPurchaseUnit || null,
			skuType: clonedState.fields.productClass.reduce(
				(finalType, currType) => {
					if (currType.isApplied) {
						return currType.name.toUpperCase();
					}
					return finalType;
				},
				''
			),
		};

		return imageToBase64(req.data.productImageUrl).then((base64Image) => {
			req.data.productImageUrl = base64Image;
			return req;
		});
	},
	validator: (state) => {
		// eslint-disable-next-line object-curly-newline
		const {
			image,
			price,
			tax,
			brand,
			density,
			category,
			subCategory,
			productClass,
			productUnits,
			purchaseUnits,
			hsnCode,
		} = state.fields;

		// check if image is uploaded or not
		if (!image) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please upload an image for the product',
			});
			return false;
		}

		const unit = productUnits?.reduce((finalUnit, currUnit) => {
			if (currUnit?.isApplied) {
				return currUnit?.id;
			}
			return finalUnit;
		}, '');

		if (unit === 'ml' || unit === 'floz' || unit === 'litre') {
			// eslint-disable-next-line eqeqeq
			if (!density || density <= 0) {
				notificationServices.generateNotification({
					type: 'error',
					message: 'Density mandatory for liquid items',
				});
				return false;
			}
		}
		const purchaseUnit = purchaseUnits?.reduce((finalUnit, currUnit) => {
			if (currUnit?.isApplied) {
				return currUnit?.id;
			}
			return finalUnit;
		}, null);
		if (purchaseUnit && purchaseUnit === unit) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Product Unit and Purchase Unit cannot be same.',
			});
			return false;
		}

		// check if price is greater than 0
		if (!price || price <= 0) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Product price cannot be zero',
			});
			return false;
		}

		// check if tax is within 0-100
		if (tax > 100 || tax < 0) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Product tax should be within 0-100',
			});
			return false;
		}

		if (!hsnCode) {
			const isIntermediate = productClass?.some(
				(type) => type.name === 'Intermediate' && type.isApplied === true
			);
			if (!isIntermediate) {
				notificationServices.generateNotification({
					type: 'error',
					message: 'HSN Code is Mandatory',
				});
				return false;
			}
		}
		if (hsnCode && (hsnCode.length !== 4 && hsnCode.length !== 6 && hsnCode.length !== 8)) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'HSN Code length must be either 4 or 6 digits.',
			});
			return false;
		}

		// check if brand is selected or not
		if (brand == null || brand === '') {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select a valid Brand',
			});
			return false;
		}

		// check if category is selected or not
		if (category == null || category === '') {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select a valid Category',
			});
			return false;
		}
		// check if sub category is selected or not
		if (subCategory == null || subCategory === '') {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select a valid Sub Category',
			});
			return false;
		}

		// check if product class is selected or not
		const noProductClass = productClass.every((productClassObject) => {
			return !productClassObject.isApplied;
		});
		if (noProductClass) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select a product type',
			});
			return false;
		}

		// check if product class is selected or not
		const noProductUnitSelected = productUnits.every(
			(productUnitObject) => {
				return !productUnitObject.isApplied;
			}
		);
		if (noProductUnitSelected) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select product unit',
			});
			return false;
		}

		return true;
	},
};

export default productConfig;
